import { createApiRef } from '@backstage/core-plugin-api';

export interface GeminiAgentSession {
  id: string;
  userId: string;
  repository: string;
  branch: string;
  prompt?: string;
  accessGroup?: string;
  cloudRunServiceName?: string;
  status: 'creating' | 'running' | 'stopped' | 'failed' | 'deleted';
  cloudRunUrl?: string;
  gcpProject?: string;
  workspaceBucketPath?: string;
  keepIndefinitely?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserIdentity {
  githubUsername?: string;
  email?: string;
  fullName?: string;
}

export interface CreateSessionRequest {
  repository: string;
  branch: string;
  prompt?: string;
  accessGroup?: string;
  selectedCredentials?: string[];
  keepIndefinitely?: boolean;
  interactiveMode?: boolean;
  interactiveTtlMinutes?: number;
  enableWorkspaceStorage?: boolean;
  userIdentity?: UserIdentity;
}

export interface CreateSessionOptions {
  githubToken?: string;
  googleAccessToken?: string;
}

export interface GcpWarning {
  message: string;
  detail: string;
  project?: string;
}

export interface ListSessionsOptions {
  includeInactive?: boolean;
  mine?: boolean;
}

export interface SessionListResult {
  sessions: GeminiAgentSession[];
  currentUserId?: string;
  warning?: GcpWarning;
}

export interface CredentialInfo {
  id: string;
  label: string;
  source: 'oauth' | 'vault' | 'config';
  available: boolean;
  error?: string;
}

export interface CredentialsCheckResult {
  credentials: CredentialInfo[];
  vaultReachable: boolean;
  vaultError?: string;
}

export interface CheckCredentialsOptions {
  githubToken?: string;
  googleAccessToken?: string;
}

export interface LogEntry {
  timestamp: string;
  severity: string;
  message: string;
}

export interface StoredChatMessage {
  id: number;
  sessionId: string;
  role: string;
  text: string;
  timestamp: number;
}

export interface StoredActivityEvent {
  id: number;
  sessionId: string;
  kind: string;
  state?: string;
  subject?: string;
  description?: string;
  message?: string;
  timestamp: number;
}

export interface ChatHistory {
  chatMessages: StoredChatMessage[];
  activityEvents: StoredActivityEvent[];
  queueStatus?: { pending: number; processing: number; completed: number; failed: number };
  pendingMessages?: Array<{ id: number; text: string; status: string }>;
}

export interface UpdateSessionRequest {
  keepIndefinitely?: boolean;
  interactiveMode?: boolean;
  interactiveTtlMinutes?: number;
}

// ── Prompt Library ──────────────────────────────────────────────────

export interface PromptVariable {
  name: string;
  description?: string;
  default?: string;
}

export interface SessionConfig {
  interactiveTtlMinutes?: number;
  interactiveMode?: boolean;
  selectedCredentials?: string[];
  keepIndefinitely?: boolean;
}

export interface StoredPrompt {
  id: string;
  displayName: string;
  description?: string;
  promptText: string;
  systemInstruction?: string;
  model?: string;
  generationConfig?: Record<string, unknown>;
  sessionConfig?: SessionConfig;
  labels: string[];
  variables?: PromptVariable[];
  ownerId: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePromptRequest {
  displayName: string;
  description?: string;
  promptText: string;
  systemInstruction?: string;
  model?: string;
  generationConfig?: Record<string, unknown>;
  sessionConfig?: SessionConfig;
  labels?: string[];
  variables?: PromptVariable[];
}

export interface UpdatePromptRequest {
  displayName?: string;
  description?: string;
  promptText?: string;
  systemInstruction?: string;
  model?: string;
  generationConfig?: Record<string, unknown>;
  sessionConfig?: SessionConfig;
  labels?: string[];
  variables?: PromptVariable[];
}

export interface ListPromptsOptions {
  labels?: string[];
  owner?: 'mine';
  search?: string;
}

export interface PromptListResult {
  prompts: StoredPrompt[];
}

export interface HealthStatus {
  status: string;
  localMode?: boolean;
  workspaceStorageAvailable?: boolean;
}

export interface GeminiAgentApi {
  getHealth(): Promise<HealthStatus>;
  listSessions(options?: ListSessionsOptions): Promise<SessionListResult>;
  createSession(
    request: CreateSessionRequest,
    options?: CreateSessionOptions,
  ): Promise<GeminiAgentSession>;
  getSession(sessionId: string): Promise<GeminiAgentSession>;
  deleteSession(sessionId: string): Promise<void>;
  /** Enqueue a message for background processing by the server. */
  enqueueMessage(sessionId: string, text: string): Promise<void>;
  /** Fetch recent container logs in batch mode. */
  fetchLogs(sessionId: string, limit?: number): Promise<LogEntry[]>;
  checkCredentials(
    options?: CheckCredentialsOptions,
  ): Promise<CredentialsCheckResult>;
  /** Fetch stored chat history from the database (includes queue status). */
  getChatHistory(sessionId: string): Promise<ChatHistory>;
  /** Update session settings. */
  updateSession(sessionId: string, request: UpdateSessionRequest): Promise<void>;
  /** Cancel the in-flight task without stopping the session. */
  cancelTask(sessionId: string): Promise<void>;

  // Prompt Library
  listPrompts(options?: ListPromptsOptions): Promise<PromptListResult>;
  getPrompt(promptId: string): Promise<StoredPrompt>;
  createPrompt(request: CreatePromptRequest): Promise<StoredPrompt>;
  updatePrompt(promptId: string, request: UpdatePromptRequest): Promise<StoredPrompt>;
  deletePrompt(promptId: string): Promise<void>;
}

export const geminiAgentApiRef = createApiRef<GeminiAgentApi>({
  id: 'plugin.gemini-agent.api',
});
